import Vue from 'vue';
import Router from 'vue-router';
import Home from './views/Home.vue';
import Dashboard from '@/views/Dashboard.vue';
import store from './store';
import {RepositoryFactory} from '@/api/RepositoryFactory';

const UserRepository = RepositoryFactory.get('users');
const GroupsRepository = RepositoryFactory.get('groups');
const SalonsRepository = RepositoryFactory.get('salons');
const DevicesRepository = RepositoryFactory.get('devices');
const ConfigurationRepository = RepositoryFactory.get('configuration');

Vue.use(Router);

const router = new Router({
  mode: 'history',
  base: process.env.BASE_URL,
  routes: [
    {
      path: '*',
      name: 'redirect',
      component: () => import('./views/Dashboard.vue'),
      meta: {
        requiresAuth: true,
      },
    },
    {
      path: '/',
      name: 'home',
      component: Dashboard,
      meta: {
        requiresAuth: true,
      },
    },
    {
      path: '/about',
      name: 'about',
      component: () => import('./views/About.vue'),
    },
    {
      path: '/login',
      name: 'login',
      component: () => import('./components/auth/Login.vue'),
    },
    {
      path: '/forgot-password',
      name: 'forgot-password',
      component: () => import('./components/auth/PasswordReset.vue'),
    }, {
      path: '/password-reset/reset',
      name: 'reset-password',
      component: () => import('./components/auth/PasswordResetStep2.vue'),
    },
    {
      path: '/kek',
      name: 'kek',
      component: () => import('./views/Dashboard.vue'),
      meta: {
        requiresAuth: true,
      },
    },
    {
      path: '/dashboard',
      name: 'dashboard',
      component: Dashboard,
      meta: {
        requiresAuth: true,
      },
      children: [
        {
          path: '',
          name: 'dashboard-main',
          component: Home,
        },
        {
          path: '/myAccount',
          name: 'account-edit',
          component: () => import('./views/myAccount/Account.vue'),
        },
        {
          path: '/administration',
          name: 'administration',
          component: () => import('./views/Administration.vue'),
          meta: {
            role: 'admin',
          },
          children: [
            {
              path: 'users',
              name: 'administer-users',
              component: () => import('./views/Users/UsersList.vue'),
            },
            {
              path: 'edit/user/:user',
              name: 'edit-user',
              component: () => import('./views/Users/EditUser.vue'),
              props: true,
            },
            {
              path: 'register/user',
              name: 'register-user',
              component: () => import('./views/Users/RegisterUser.vue'),
              props: true,
            },
            {
              path: 'groups',
              name: 'manage-groups',
              component: () => import('./views/Groups/GroupsList.vue'),
            },
            {
              path: 'salons',
              name: 'manage-salons',
              component: () => import('./views/Salons/SalonsList.vue'),
            },
            {
              path: 'devices',
              name: 'devices',
              component: () => import('./views/devices/DevicesList.vue'),
            },
          ],
        },
        {
          path: '/weather',
          name: 'weather',
          component: () => import('./views/Weather/Weather.vue'),
        },
        {
          path: '/reports',
          name: 'reports',
          component: () => import('./views/Reports.vue'),
          children: [
            {
              path: 'cumulative',
              name: 'cumulative-reports',
              component: () => import('./views/Reports/Cumulative.vue'),
            }, {
              path: 'report-manager-config',
              name: 'report-manager-config',
              meta: {
                role: 'supervisor',
              },
              component: () => import('./views/reportManager/reportsList.vue'),
            },
            {
              path: 'signals/:type',
              name: 'signals',
              component: () => import('./views/Reports/Live.vue'),
            }, {
              path: 'unit/:id',
              name: 'unit',
              component: () => import('./views/Reports/Unit.vue'),
            },
            {
              path: 'analysis',
              name: 'analysis',
              component: () => import('./views/Reports/Analysis.vue'),
            },
          ],
        },
        {
          path: '/localization',
          name: 'localization',
          component: () => import('./views/localization/Localization.vue'),
        }, {
          path: '/configuration',
          name: 'configuration',
          component: () => import('./views/configuration/Configuration.vue'),
        },
        {
          path: '/contact',
          name: 'contact',
          component: () => import('./views/help/Help.vue'),
        }, {
          path: '/help',
          name: 'help',
          component: () => import('./views/contact/ContactForm.vue'),
        },
      ],
    },
  ],
});

router.beforeEach(async (to, from, next) => {
  await store.dispatch('navigating', true);
  if (to.matched.some(record => record.meta.requiresAuth)) {
    let token = localStorage.getItem('token');
    if (!token) {
      token = store.getters.token;
    }
    if (store.getters.isLoggedIn) {
      store.commit('set_api_call', false);
      if (to.matched.some(record => record.meta.role)) {
        const { role } = to.matched.find(record => record.meta.role).meta;
        if (role === 'admin') {
          if (store.getters.isAdmin) {
            store.commit('set_store_state', true);
            await store.dispatch('navigating', false);
            next();
          } else {
            store.commit('set_store_state', true);
            await store.dispatch('navigating', false);
            next(false);
          }
        }
        if (role === 'supervisor') {
          if (store.getters.isAdmin || store.getters.isDirector) {
            store.commit('set_store_state', true);
            await store.dispatch('navigating', false);
            next();
          } else {
            store.commit('set_store_state', true);
            await store.dispatch('navigating', false);
            next(false);
          }
        }
      } else {
        store.commit('set_store_state', true);
        await store.dispatch('navigating', false);
        next();
      }
    } else {
      try {
        store.commit('set_store_state', false);
        await store.dispatch('setToken', token);
        const configuration = await ConfigurationRepository.getConfiguration(token);
        await store.dispatch('setConfig', configuration);
        const user = await UserRepository.getUser(token);
        user.groups = await GroupsRepository.getContextualGroups(token);
        await store.dispatch('setUser', { user });
        const salons = await SalonsRepository.getShopsGroup(token, store.getters.selectedGroup.id);
        await store.dispatch('setSalons', salons);
        if (store.getters.role === 'ADMINISTRATOR' || store.getters.role === 'OPTIGUARD') {
          const devices = await DevicesRepository.getDevices(token, store.getters.selectedGroup.id);
          await store.dispatch('setDevices', devices);
        }
        store.commit('set_store_state', true);
        if (to.path === '/') {
          next('/dashboard');
        } else if (to.matched.some(record => record.meta.role)) {
          const { role } = to.matched.find(record => record.meta.role).meta;
          if (role === 'admin') {
            if (store.getters.isAdmin) {
              store.commit('set_store_state', true);
              await store.dispatch('navigating', false);
              next();
            } else {
              store.commit('set_store_state', true);
              await store.dispatch('navigating', false);

              next('/dashboard');
            }
          }
        } else {
          store.commit('set_store_state', true);
          await store.dispatch('navigating', false);

          next();
        }
      } catch (e) {
        store.commit('set_expired', true);
        await store.dispatch('logout');
        next('/login');
      }
    }
  } else {
    next();
  }
});

export default router;
